import { useState } from "react";
import { Text, TextInput } from "@mantine/core";

export function JointNameEditor({
  joint,
  onRename,
  onHighlight,
  onClearHighlight,
}: {
  joint: { name: string; display_name: string | null };
  onRename: (newName: string) => void;
  onHighlight: () => void;
  onClearHighlight: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  // Get display name - show full name with prefix (e.g., "arm1/joint1")
  const getDisplayName = () => {
    if (joint.display_name) {
      return joint.display_name;
    }
    // Show full unique name including prefix (prefix/joint_name)
    return joint.name;
  };

  const handleStartEdit = () => {
    setEditValue(getDisplayName());
    setEditing(true);
  };

  const handleFinishEdit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== getDisplayName()) {
      onRename(trimmed);
    }
    setEditing(false);
    onClearHighlight();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleFinishEdit();
    } else if (e.key === "Escape") {
      setEditing(false);
      onClearHighlight();
    }
  };

  const handleClick = () => {
    onHighlight();
    handleStartEdit();
  };

  if (editing) {
    return (
      <TextInput
        size="xs"
        value={editValue}
        onChange={(e) => setEditValue(e.currentTarget.value)}
        onBlur={handleFinishEdit}
        onKeyDown={handleKeyDown}
        autoFocus
        style={{ minWidth: 0, flex: 1, fontFamily: "monospace" }}
      />
    );
  }

  return (
    <Text
      size="xs"
      c="dimmed"
      truncate="start"
      style={{
        fontFamily: "monospace",
        cursor: "pointer",
        textDecoration: "underline dotted",
        // Without a zero min-width a flex item refuses to shrink below its
        // text, so a long "instance/joint" name pushed the angle field on top
        // of it instead of ellipsing. Truncating from the start keeps the
        // joint name visible and drops the instance prefix, which is the
        // repeated part.
        minWidth: 0,
        flex: 1,
      }}
      onClick={handleClick}
      onMouseEnter={onHighlight}
      onMouseLeave={onClearHighlight}
      title="Click to rename, hover to highlight"
    >
      {getDisplayName()}
    </Text>
  );
}

